"use client";

import * as React from "react";

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import UILink from "@/components/ui/link";
import { Page } from "@/types";

interface ReferencesProps {
	references: Array<Object>;
	accordionsState?: any;
	asAccordion?: boolean;
	page?: Page;
}

function useHash() {
	const [hash, setHash] = React.useState("");

	React.useEffect(() => {
		const handleHashChange = () => setHash(window.location.hash);

		handleHashChange();
		window.addEventListener("hashchange", handleHashChange);

		return () => window.removeEventListener("hashchange", handleHashChange);
	}, []);

	return hash;
}

function useReferenceExpand(hash: string) {
	const [value, setValue] = React.useState<string>();

	React.useEffect(() => {
		if (hash.includes("ref-")) {
			setValue("references");
		}
	}, [hash]);

	React.useEffect(() => {
		if (value === "references" && hash.includes("ref-")) {
			const refId = hash.slice(1);
			const element = document.getElementById(refId);
			element?.scrollIntoView({ behavior: "smooth" });
		}
	}, [value, hash]);

	return [value, setValue] as const;
}

const References = ({ references, asAccordion = true, page }: ReferencesProps) => {
	const hash = useHash();
	const [value, setValue] = useReferenceExpand(hash);

	if (!references?.length) return null;

	if (!asAccordion) {
		return (
			<section id="references" className="space-y-4 -tracking-2">
				<h2 className="mb-4 text-lg font-bold">References</h2>
				<ol className="relative ml-10 w-full list-decimal space-y-6 py-4">
					{references?.map((item: any) => (
						<li key={item?.ref_id} id={`ref-${item?.ref_id}`} className="scroll-mt-28">
							<a href={`#ref-link-${item?.ref_id}`} className="px-2 text-primary underline">
								^
							</a>
							<span className="pr-2">{item?.authors}</span>
							<UILink
								href={item?.url || "#"}
								target="_blank"
								rel="noreferrer"
								className="text-primary"
							>
								{item?.title}
							</UILink>
							{item?.journal ? <span className="px-2">{item?.journal}.</span> : null}
							{item?.published ? <span>({item?.published})</span> : null}
						</li>
					))}
				</ol>
			</section>
		);
	}

	return (
		<Accordion type="single" collapsible value={value} onValueChange={setValue}>
			<AccordionItem value="references" id="references">
				<AccordionTrigger
					copyLinkUrl={page && (page?.isPaidAd ? "#0" : `${page?.url}/#references`)}
					as="h2"
					className="group flex items-center border-b border-dashed border-current pb-2 md:text-30 3xl:text-40"
				>
					<span>References</span>
				</AccordionTrigger>

				<AccordionContent className="overflow-visible">
					<ol className="relative ml-5 w-full list-decimal space-y-6 py-4">
						{references?.map((item: any) => (
							<li key={item?.ref_id} id={`ref-${item?.ref_id}`} className="scroll-mt-28">
								<a href={`#ref-link-${item?.ref_id}`} className="px-2 text-primary underline">
									^
								</a>
								<span className="pr-2">{item?.authors}</span>
								<UILink
									href={item?.url || "#"}
									target="_blank"
									rel="noreferrer"
									className="text-primary"
								>
									{item?.title}
								</UILink>
								{item?.journal ? <span className="px-2">{item?.journal}.</span> : null}
								{item?.published ? <span>({item?.published})</span> : null}
							</li>
						))}
					</ol>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
};

const EDBReferences = ({ references, page }: ReferencesProps) => {
	if (!references?.length) return null;

	return (
		<Accordion id="edb-references" type="single" defaultValue="edb-references" collapsible>
			<AccordionItem value="edb-references">
				<AccordionTrigger
					copyLinkUrl={page?.isPaidAd ? "#0" : `${page?.url}/#edb-references`}
					as="h2"
					className="group flex items-center border-b border-dashed border-current pb-2 md:text-30 3xl:text-40"
				>
					<span>Examine Database References</span>
				</AccordionTrigger>

				<AccordionContent className="overflow-visible">
					<ol className="relative ml-5 w-full list-decimal space-y-6 py-4">
						{references?.map((item: any, idx) => (
							<li key={idx} className="scroll-mt-28">
								<span>{item?.parent?.name} - </span>
								<span className="pr-2">{item?.authors}</span>
								<UILink href={item?.url} target="_blank" rel="noreferrer" className="text-primary">
									{item?.title}
								</UILink>
								<span className="px-2">{item?.journal}.</span>
								<span>({item?.published})</span>
							</li>
						))}
					</ol>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
};

export { References, EDBReferences };
